import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginRequest } from "../../../../Redux/Slices/Common/commonSlice";
import "./styles.scss";
import AuthLayout from "Layouts/Auth";
import { notifySuccess } from "Services/Utils/toastNotification";
import { getDeviceId } from "Services/Utils/commonServices";
import InputBox from "Components/InputBox";
import Button from "Components/Button";
import ErrorMessage from "Components/ErrorMessage";
import { requestPermissionAndSendToken } from "Utils/firebaseConfig";

const Login = () => {
  // Initialize redux and navigation
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State management
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    otp: ""
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoginVisible, setIsLoginVisible] = useState(true);
  const MAX_EMAIL_LENGTH = 254;
  const MAX_PASSWORD_LENGTH = 50;

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "email") {
      const trimmedValue = value.trimStart().slice(0, MAX_EMAIL_LENGTH);
      setFormData(prev => ({ ...prev, [name]: trimmedValue }));
    } else if (name === "password") {
      const limitedValue = value.slice(0, MAX_PASSWORD_LENGTH);
      setFormData(prev => ({ ...prev, [name]: limitedValue }));
    }

    // Special handling for OTP to allow only numbers
    if (name === 'otp') {
      // Replace any non-digit characters
      const numericValue = value.replace(/\D/g, '');

      // Limit to 6 digits
      const limitedValue = numericValue.slice(0, 6);

      setFormData(prev => ({
        ...prev,
        [name]: limitedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    const emailTrimmed = formData.email.trim();

    if (isLoginVisible) {
      // Email validation
      if (!emailTrimmed) {
        newErrors.email = "Email is required";
      } else if (!/^[^@]+@[^@]+\.[^@]+$/.test(emailTrimmed)) {
        newErrors.email = "Invalid email address";
      }

      // Password validation
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters long";
      }
    } else {
      // OTP validation
      if (!formData.otp) {
        newErrors.otp = "OTP is required";
      } else if (!/^\d{6}$/.test(formData.otp)) {
        newErrors.otp = "OTP must be exactly 6 digits";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle initial login submission
  const onSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) return;

    setIsSubmitting(true);
    dispatch(
      loginRequest({
        data: {
          identifier: formData.email.trim(),
          password: formData.password,
          device_token: getDeviceId(),
        },
        callBack: loginRequestCB,
      })
    );
  };

  // Handle OTP submission
  const onSubmitOTP = (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) return;

    setIsSubmitting(true);
    dispatch(
      loginRequest({
        data: {
          identifier: formData.email,
          password: formData.password,
          otp: formData.otp,
          device_token: getDeviceId(),
        },
        callBack: loginRequestCB,
      })
    );
  };

  // Callback for login request
  const loginRequestCB = (data) => {
    setIsSubmitting(false);

    if (data?.message === "Otp sent successfully!" && data?.status === 200) {
      setIsLoginVisible(false);
      notifySuccess("OTP sent successfully! Please enter the OTP to continue.");
      return;
    }

    if (data.status === 400) {
      // Handle error case
      return;
    }

    if (data.status !== 400) {
      notifySuccess("Login successful!");
      localStorage?.setItem("isLoggedIn", true);

      // Reset form
      setFormData({
        email: "",
        password: "",
        otp: ""
      });

      if (data?.data?.first_login) {
        navigate("/create-password");
      } else {
        navigate("/");
        requestPermissionAndSendToken();
      }
    }
  };

  // Handle back to login
  const handleBacktoLogin = () => {
    setIsLoginVisible(true);
    setIsSubmitting(false);
    setErrors({});

    // Clear password when going back to login
    setFormData(prev => ({
      ...prev,
      password: "",
      otp: ""
    }));
  };

  // Styling
  const input_box = {
    width: "85%",
    paddingRight: "2.5rem",
    fontSize: "16px",
  };

  const eye_icon = {
    position: "absolute",
    top: "50%",
    right: "17%",
    transform: "translateY(-50%)",
    cursor: "pointer",
  };

  return (
    <AuthLayout>
      <div className="user-login-root-container">
        <h1 className="user-login-heading">
          {isLoginVisible ? "Login to your Account!" : "Enter the OTP"}
        </h1>
        <p className="user-login-sub-heading">
          {isLoginVisible
            ? "Login to access your account"
            : "Enter the OTP sent to your email"}
        </p>
        <hr className="user-login-hr" />

        <form onSubmit={isLoginVisible ? onSubmit : onSubmitOTP}>
          {isLoginVisible ? (
            <>
              <InputBox
                label="Email ID"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                inputStyle={input_box}
                maxLength={254}
              />
              <ErrorMessage message={errors.email} />
              <div className="user-login-spacer" />

              <InputBox
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                inputStyle={input_box}
                eyeIconStyle={eye_icon}
                maxLength={50}
              />
              <ErrorMessage message={errors.password} />

              <div className="user-login-forgot-password-container">
                <div onClick={() => navigate("/contact-us")} onKeyDown={(e) => e.key === "Enter" && navigate("/contact-us")}>
                  <p className="user-login-forgot-password-text">
                    Contact us
                  </p>
                </div>
                <div onClick={() => navigate("/forgot-password")}>
                  <p className="user-login-forgot-password-text" onKeyDown={(e) => e.key === "Enter" && navigate("/forgot-password")}>
                    Forgot Password?
                  </p>
                </div>
              </div>

              <Button
                title="Login"
                buttonStyle={{ width: "85%" }}
                disabled={isSubmitting}
                forForm
              />
            </>
          ) : (
            <>
              <InputBox
                label="OTP"
                name="otp"
                value={formData.otp}
                onChange={handleInputChange}
                inputStyle={input_box}
              />
              <ErrorMessage message={errors.otp} />

              <div className="user-login-forgot-password-container">
                <div onClick={handleBacktoLogin}>
                  <p className="user-login-forgot-password-text">
                    Back to Login
                  </p>
                </div>
              </div>

              <Button
                title="Verify OTP"
                buttonStyle={{ width: "85%" }}
                disabled={isSubmitting}
                forForm
              />
            </>
          )}
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;